import { ArrowSquareOut } from "@phosphor-icons/react"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"

import { Card } from "@tokenterminal/core/ui/layout/card/Card"
import { toDictionary } from "@tokenterminal/core/utils/toDictionary"
import { type RouterOutputs } from "@tokenterminal/core/utils/trpc"

import { useMarketSectorsList } from "modules/chart-builder/components/SeriesTab/SerieSelector/FilterSelector/useMarketSectorsList"
import { iconsBySlug } from "modules/market-sectors/constants"
import { type MetricConfiguration } from "modules/projects/hooks/useMetricsConfigurationList"
import Link from "next/link"
import MarketShareAreaChart from "./MetricMarketShareChart"

// Number of top projects to show in the metric chart
const MAX_PROJECTS = 10
import { ProjectsTable } from "./ProjectsTable"

const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

const getMarketSectorsByMetricSlug = (metricSlug: string) => {
  const lookup: Record<string, string[]> = {
    "capital-deployed": ["asset-management"],
    fees: [
      "blockchains",
      "blockchains-l1",
      "gaming",
      "infrastructure",
      "oracles",
      "social",
    ],
    "bridge-deposits": ["blockchains-l2", "blockchains-l3"],
    "transfer-volume": ["bridges", "interoperability"],
    "notional-trading-volume": ["derivatives"],
    "trading-volume": ["exchange", "nft", "prediction-markets"],
    "transaction-volume": ["insurance"],
    "active-loans": ["lending"],
    "assets-staked": ["liquid-staking"],
    "outstanding-supply": ["stablecoin"],
    // Ecosystem
    gdp: ["blockchains-l1", "blockchains-l2"],
    "ecosystem-tvl": ["blockchains-l1", "blockchains-l2"],
    "ecosystem-fees": ["blockchains-l1", "blockchains-l2"],
    "ecosystem-dex-trading-volume": ["blockchains-l1", "blockchains-l2"],
  }

  return lookup[metricSlug] || []
}

const MarketSectorTag = ({
  marketSector,
}: {
  marketSector:
    | RouterOutputs["marketSectors"]["getMarketSectors"]["data"][number]
    | undefined
}) => {
  if (!marketSector) {
    return null
  }

  return (
    <Link
      className="inline-flex gap-1.5 text-sm py-0.5 px-2 items-center justify-between whitespace-nowrap rounded-[6px] bg-neutral-500/10 dark:bg-neutral-500/30"
      href={`/markets/${marketSector.slug}`}
    >
      <Icon as={iconsBySlug[marketSector.slug]} className="text-fg-secondary" />
      {marketSector.name}
    </Link>
  )
}

const MetricSourcesText = ({ metric }: { metric: MetricConfiguration }) => {
  const sourcesLinkLookup: Record<string, string> = {
    CoinGecko: "https://www.coingecko.com",
    CoinMarketCap: "https://www.coinmarketcap.com",
  }

  const sources = metric.sources.split(/,\s*/)

  return sources.map((source, index) => {
    let text = <span>{source}</span>
    if (sourcesLinkLookup[source]) {
      text = (
        <a
          target="_blank"
          rel="noopener"
          href={sourcesLinkLookup[source]}
          className="inline-flex items-center gap-1 hover:underline text-fg-blue"
        >
          {source}
          <Icon as={ArrowSquareOut} />
        </a>
      )
    }

    return [
      index > 0 && <span className="text-fg-secondary">,&nbsp;</span>,
      text,
    ]
  })
}

type OverviewTabProps = {
  metric: RouterOutputs["metrics"]["getMetricConfiguration"]
  isEcosystemMetric: boolean
}

const OverviewTab = ({ metric, isEcosystemMetric }: OverviewTabProps) => {
  const relatedMarketSectors = getMarketSectorsByMetricSlug(metric.slug)
  const { data: marketSectors = [], isLoading: isMarketSectorsLoading } =
    useMarketSectorsList()
  const marketSectorsById = toDictionary(marketSectors, "id")

  const hasRelatedMarketSectors = relatedMarketSectors.length > 0
  return (
    <div className="flex flex-col gap-10 w-full">
      <div className="rounded-2xl grow">
        <div className="flex flex-col gap-6 lg:gap-8">
          <div className="col-span-1 lg:col-span-4 flex flex-col gap-1">
            <p className="w-full text-pretty text-fg-secondary text-sm max-w-[1000px]">
              {capitalizeFirstLetter(metric.definition)}
            </p>
          </div>
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6 lg:gap-16">
            <div className="col-span-1 lg:col-span-2 flex flex-col gap-1">
              <label className="text-sm text-fg-secondary">Sources</label>
              <p className="text-sm">
                <MetricSourcesText metric={metric} />
              </p>
            </div>
            {hasRelatedMarketSectors && (
              <div className="col-span-1 lg:col-span-2 flex flex-col gap-1">
                <label className="text-sm text-fg-secondary">
                  Related market sectors
                </label>
                <div
                  className="flex flex-wrap gap-2 min-h-6"
                  style={{
                    opacity: isMarketSectorsLoading ? 0 : 1,
                    transition: "opacity 0.3s ease-in-out",
                  }}
                >
                  {relatedMarketSectors.map((sectorId) => (
                    <MarketSectorTag
                      key={sectorId}
                      marketSector={marketSectorsById.get(sectorId)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Card className="rounded-2xl p-6 grow">
        <MarketShareAreaChart
          metricSlug={metric.slug}
          isStatic={metric.static}
          metricTitle={metric.title}
          granularity="week"
          interval="365d"
          isEcosystemMetric={isEcosystemMetric}
          maxProjects={MAX_PROJECTS}
        />
      </Card>
      <ProjectsTable metricId={metric.metric_id} isStatic={metric.static} />
    </div>
  )
}

export default OverviewTab
